// Read from the Figma component set "Modal" (14253:235790) in [Web] Dialog Windows.
//
// Hand-written, unlike the primitive contracts: this is the first pattern, and
// it is here to find out what `tokens:import` has to learn before it can emit
// one. Three things it cannot express today are recorded below — `composes`,
// `unbound`, and the fact that the two variants differ in *structure*, not only
// in tokens. See PATTERN-NOTES.md.

export type ContractProp =
    | { kind: 'text' }
    | { kind: 'boolean' }
    | { kind: 'variant'; values: readonly string[] }

/** A value Figma draws but binds to nothing, so no token can carry it. */
export type UnboundValue = {
    node: string
    property: string
    value: number | string
    /** The live token holding the same value, when one exists. */
    matches?: string
    why: string
}

export const modalContract = {
    component: 'Modal',
    nodeId: '14253:235790',
    section: '[Web] Dialog Windows',

    // h1-regular for the title, p1-regular for the body.
    textStyleIds: [
        'S:7128ca49edc77e1b4cfbcf2d61aa135d736752a2,',
        'S:951e86eed2678091c373ab50c6cceb638c129b9d,',
    ],

    props: {
        variant: { kind: 'variant', values: ['default', 'destructive'] },
        showSecondaryButton: { kind: 'boolean' },
    },

    // The union across both variants. `medium-less-dense/m` appears only in
    // `default`, because `destructive` moves the title into the header and the
    // gap it spaced stops existing.
    //
    // The two variants used to pad differently — 3xl against 4xl — which is one
    // of the questions that went to design. They unified it on 3xl, so there is
    // no fourth step here any more.
    figmaTokens: [
        'background/sub-surface',
        'background/surface',
        'content/default',
        'large-layout/2xl',
        'large-layout/3xl',
        'medium-less-dense/m',
        'message-mobile',
    ],

    /**
     * Other aos-ds components this one instantiates. A primitive contract has
     * no such field: nothing below this tier composes anything.
     */
    composes: [
        {
            component: 'Button',
            variants: [
                'size=small, type=primary',
                'size=small, type=secondary',
                'size=small, type=destructive',
            ],
        },
    ],

    /**
     * Figma draws these but binds none of them. Each raw number happens to
     * equal a live token today; `modal.contract.spec.ts` proves that equality
     * rather than trusting it, and the questions go to design.
     */
    unbound: [
        {
            node: 'header/content',
            property: 'itemSpacing',
            value: 12,
            matches: 'medium-less-dense/m',
            why: 'the gap between the illustration and the title is a raw number',
        },
        {
            node: 'content/content',
            property: 'itemSpacing',
            value: 24,
            matches: 'large-layout/2xl',
            why: 'the gap between the body text and the buttons is a raw number',
        },
        {
            node: 'content/content/buttons',
            property: 'itemSpacing',
            value: 24,
            matches: 'large-layout/2xl',
            why: 'the gap between the two buttons is a raw number',
        },
        {
            node: 'FeedOutConfirmation',
            property: 'fills',
            value: '#ffffff',
            why: 'a 100x100 illustration whose five vectors carry a hard-coded white; no token, and no asset class in aos-ds for something this size. The component takes it as a prop instead of shipping it.',
        },
    ] as readonly UnboundValue[],
} as const
